import { Carousel, LoopButton, Typography } from '@loophealth/loop-ui-web-library';
import React, { useState } from 'react';
import {
    StyledOnboardingScreenCarousel,
    StyledOnboardingScreenCTA,
    StyledOnboardingScreenFtuxImageBg,
    StyledOnboardingScreenFtuxItem,
    StyledOnboardingScreenFtuxWrapper,
    StyledOnboardingScreenImage,
    StyledOnboardingScreenItemColumn,
    StyledOnboardingScreenItemHeader,
    StyledOnboardingScreenItemHeaderBadge,
    StyledOnboardingScreenItemHeaderBadgeWrapper,
    StyledRowItems
} from './styles';
import { IOnboardingScreenFtuxProps } from './types';
import { OnboardingScreenFtuxSteps } from './constants';
import { useSelector } from 'react-redux';
import { selectUserData } from '../../pages/LoginContainer/redux/slices/UserSlice/selectors';
import { formatFullName } from '../../../utils/common';
import LoopTopLeftLogo from '../../atoms/LoopTopLeftLogo';
import LoopTopRightUserMenu from '../../atoms/LoopTopRightUserMenu';
import { AnimatePresence } from 'framer-motion';

const OnboardingScreenFtux: React.FC<IOnboardingScreenFtuxProps> = ({ onClose }) => {
    const [currentStep, setCurrentStep] = useState(0);
    const user = useSelector(selectUserData);
    const name = formatFullName(user.data.firstName);

    const totalSteps = OnboardingScreenFtuxSteps.length;
    const isLastStep = currentStep === totalSteps - 1;
    const currentStepItem = OnboardingScreenFtuxSteps[currentStep];

    const handleNextClick = () => {
        if (isLastStep) onClose();
        else setCurrentStep(currentStep + 1);
    };

    const handleBackClick = () => {
        setCurrentStep(currentStep - 1);
    };

    return (
        <StyledOnboardingScreenFtuxWrapper data-testid="onboarding-screen-ftux">
            <LoopTopLeftLogo />
            <LoopTopRightUserMenu />
            <StyledOnboardingScreenFtuxItem>
                <StyledRowItems>
                    <AnimatePresence>
                        <StyledOnboardingScreenItemHeader
                            key={'step-' + currentStep}
                            initial={{ x: '50vw' }}
                            exit={{ x: '-50vw', transition: { duration: 0.4 } }}
                            animate={{ x: 0, transition: { delay: 0.4, duration: 0.4 } }}
                        >
                            <StyledOnboardingScreenItemHeaderBadgeWrapper>
                                {currentStepItem.showBadge && (
                                    <StyledOnboardingScreenItemHeaderBadge data-testid="onboarding-ftux-header-badge">
                                        <Typography variant="extraSmall" weight="medium" color="emerald">
                                            👋 Hey {name}! Welcome to Loop
                                        </Typography>
                                    </StyledOnboardingScreenItemHeaderBadge>
                                )}
                                <StyledOnboardingScreenItemColumn data-testid="onboarding-ftux-left-text">
                                    {currentStepItem.title}
                                    <Typography variant="large">{currentStepItem.subtitle}</Typography>
                                </StyledOnboardingScreenItemColumn>
                            </StyledOnboardingScreenItemHeaderBadgeWrapper>
                        </StyledOnboardingScreenItemHeader>
                    </AnimatePresence>
                </StyledRowItems>
                <StyledOnboardingScreenItemColumn data-testid="onboarding-ftux-carousel">
                    <StyledOnboardingScreenCarousel>
                        <Carousel
                            variant="points"
                            steps={totalSteps}
                            activeStep={currentStep}
                            setStep={setCurrentStep}
                        />
                    </StyledOnboardingScreenCarousel>
                    <StyledOnboardingScreenCTA data-testid="onboarding-ftux-cta">
                        {currentStepItem.backButton && (
                            <LoopButton variant="secondary" onClick={handleBackClick} width="240px">
                                Back
                            </LoopButton>
                        )}
                        <LoopButton variant="filled" onClick={handleNextClick} width="240px">
                            {currentStepItem.nextButtonText}
                        </LoopButton>
                    </StyledOnboardingScreenCTA>
                </StyledOnboardingScreenItemColumn>
            </StyledOnboardingScreenFtuxItem>
            <StyledOnboardingScreenFtuxImageBg layoutId="login-panel-container">
                <AnimatePresence>
                    <StyledOnboardingScreenImage
                        key={currentStep + 'step-image'}
                        initial={{ opacity: 0 }}
                        exit={{ opacity: 0, transition: { duration: 0.4 } }}
                        animate={{ opacity: 1, transition: { delay: 0.5, duration: 0.4 } }}
                        src={currentStepItem.image}
                        data-testid="onboarding-ftux-image"
                    />
                </AnimatePresence>
            </StyledOnboardingScreenFtuxImageBg>
        </StyledOnboardingScreenFtuxWrapper>
    );
};

export default OnboardingScreenFtux;
